import * as React from "react";
import Axios, { AxiosError } from "axios";
import { useParams } from "react-router-dom";
import { Grid, Paper } from "@mui/material";

import { Typography } from "@/components/atoms/Typography";
import Title from "@/components/molecules/Title";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { ChargerExcludeCreateEdit } from "@/components/chargerExclude/ChargerExcludeCreateEdit";
import toast from "@/components/hoc/SnackbarAlert";
import {
  ChargerExcludeConstants,
  inputDefault,
} from "./ChargerExcludeConstants";
import {
  ChargerExcludeErrorType,
  ChargerExcludeModel,
  ChargerExcludeInputModel,
  ChargerExcludeSavedState,
} from "@/models/chargerExclude";
import { Office } from "@/models/office";
import { ChargerNames } from "@/models/chargerNames";
import {
  deleteChargerExcludeAPI,
  editChargerExcludeAPI,
  getChargerExcludeDetailAPI,
} from "@/util/api/chargerExclude";
import { Messages } from "@/util/Messages";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";

export const ChargerExcludeEdit = () => {
  const [inputData, setInputData] =
    React.useState<ChargerExcludeInputModel>(inputDefault);
  const [detailData, setDetailData] = React.useState<ChargerExcludeModel>({});
  const [errorData, setErrorData] = React.useState<ChargerExcludeErrorType>({});
  const [visibleRemove, setVisibleRemove] = React.useState(false);
  const [chargerNamesSelected, setChargerNamesSelected] = React.useState<
    ChargerNames | undefined
  >();
  const [officeSelected, setOfficeSelected] = React.useState<Office>();
  const [isBeforeToday, setIsBeforeToday] = React.useState(false);
  const [inRange, setInRange] = React.useState(true);
  const { savedState, navigate } =
    useStatefulNavigator<ChargerExcludeSavedState>();

  const [editing, editChargerExcludeAPIWrapper] = useLoadingPromise(
    editChargerExcludeAPI
  );
  const [deleting, deleteChargerExcludeAPIWrapper] = useLoadingPromise(
    deleteChargerExcludeAPI
  );

  const params = useParams<{ id: string }>();
  const id = params.id;

  const goToDetail = React.useCallback(
    () =>
      navigate(`/charger_exclude/${id}`, {
        page: savedState?.page,
        searchData: savedState?.searchData,
        orderTypes: savedState?.orderTypes,
        officesSelected: savedState?.officesSelected,
      }),
    [
      id,
      navigate,
      savedState?.page,
      savedState?.searchData,
      savedState?.orderTypes,
      savedState?.officesSelected,
    ]
  );

  const goToList = React.useCallback(
    () =>
      navigate(`/charger_exclude`, {
        page: savedState?.page,
        searchData: savedState?.searchData,
        orderTypes: savedState?.orderTypes,
        officesSelected: savedState?.officesSelected,
      }),
    [
      navigate,
      savedState?.page,
      savedState?.searchData,
      savedState?.orderTypes,
      savedState?.officesSelected,
    ]
  );

  // Load charger detail to update
  const getChargerExcludeDetail = async (id: number) => {
    try {
      const res = await getChargerExcludeDetailAPI(Number(id));
      const detail = res.data;
      setDetailData(detail);
      if (!detail) return;
      //利用一時停止開始日時が更新日時より前の場合は、編集不可に設定
      if (Date.now() - new Date(detail?.startAt || "").getTime() > 0) {
        setIsBeforeToday(true);
      }
      setOfficeSelected(detail.chargers?.offices);
      setChargerNamesSelected({ name: detail.chargers?.name });
      setInputData({
        chargerId: detail.chargerId,
        startAt: detail?.startAt?.slice(0, 16) || "",
        cause: detail.cause,
      });
    } catch (err) {
      console.log("==Error when get detail charger==", err);
    }
  };

  // Handle revemo charger By API
  const onConfirmRemove = async () => {
    if (!id || editing || deleting) return;
    setVisibleRemove(false);
    deleteChargerExcludeAPIWrapper(Number(id || ""))
      .then(() => {
        goToList();
      })
      .catch((e: AxiosError<ErrorResponse>) => {
        if (
          Axios.isAxiosError(e) &&
          e.response &&
          (e.response.status === 404 || e.response.status === 409)
        ) {
          toast.error(
            CommonConstants.Delete +
              Messages.ErrorOccurred +
              "：" +
              e?.response?.data?.message
          );
        }
        console.log("==Error when delete charger exclude==", e);
      });
  };

  // Handle event click remove button
  const onClickRemove = () => {
    setVisibleRemove(true);
  };

  // Handle event cancel remove
  const onCancelRemove = () => {
    setVisibleRemove(false);
  };

  // Handle event change data charger to update
  const onChangeData = (
    key: keyof typeof inputData,
    value: string | number
  ) => {
    setInputData({ ...inputData, [key]: value });
    setErrorData({ ...errorData, [key]: false });
  };

  const handleChangeDate = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setInputData({ ...inputData, startAt: newDate });
      setErrorData({ ...errorData, startAt: false });
      setInRange(inRange ?? true);
    },
    [inputData, errorData]
  );

  // Handle submit, check validate and fetch API update
  const onSubmit = async () => {
    if (editing || deleting) {
      return;
    }

    // Check validate each field
    const errorDataTemp: ChargerExcludeErrorType = {
      cause: !inputData.cause,
      startAt: !inputData.startAt || !inRange,
    };

    // If invalid then return and show error
    if (Object.values(errorDataTemp).some((it) => it)) {
      setErrorData({ ...errorDataTemp });
      return;
    } else {
      setErrorData({});
    }

    const { ...dataSend } = inputData;

    editChargerExcludeAPIWrapper(Number(id), {
      ...dataSend,
      endAt: null,
      version: detailData.version,
    })
      .then(() => {
        goToDetail();
      })
      .catch((e: AxiosError<ErrorResponse>) => {
        if (
          Axios.isAxiosError(e) &&
          e.response &&
          (e.response.status === 404 || e.response.status === 409)
        ) {
          toast.error(
            CommonConstants.Replace +
              Messages.ErrorOccurred +
              "：" +
              e?.response?.data?.message
          );
        }
        console.log("==Error when edit charger exclude==", e);
      });
  };

  // Load charger detail when id change
  React.useEffect(() => {
    if (id) {
      getChargerExcludeDetail(Number(id));
    }
  }, [id]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{ChargerExcludeConstants.ChargerExcludeEdit}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <ChargerExcludeCreateEdit
                inputData={inputData}
                officeSelected={officeSelected}
                chargerNamesSelected={chargerNamesSelected}
                errorData={errorData}
                onChangeData={onChangeData}
                isChargerEdit
                isBeforeToday={isBeforeToday}
                onChangeDate={handleChangeDate}
              />
              <ActionButtons
                loading={editing || deleting}
                deletable
                onCancel={goToDetail}
                onSubmit={onSubmit}
                onDelete={onClickRemove}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmDialog
        visible={visibleRemove}
        headerTitle={Messages.DeleteConfirmation}
        showIcon
        type="Delete"
        onOk={() => {
          onConfirmRemove();
        }}
        onCancel={() => onCancelRemove()}
      >
        <Typography gutterBottom>
          {Messages.DeleteAreYourSureYouWantTo}
        </Typography>
      </ConfirmDialog>
    </React.Fragment>
  );
};
